import { useToggle } from "@react-hookz/web";
import first from "lodash/first";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { SearchResultListToggle } from "scmp-app/components/search/search-result-panel/search-result-list/search-result-list-toggle";
import type { topicSearchResultListTopicSearchEdge$key } from "scmp-app/queries/__generated__/topicSearchResultListTopicSearchEdge.graphql";

import { Container, StyledTopicSearchResultListItem, Title } from "./styles";

export type Props = {
  className?: string;
  reference: topicSearchResultListTopicSearchEdge$key;
};

export const TopicSearchResultList: FunctionComponent<Props> = ({ className, reference }) => {
  const topics = useFragment(
    graphql`
      fragment topicSearchResultListTopicSearchEdge on TopicSearchEdge @relay(plural: true) {
        node {
          content {
            entityId
            ...itemTopicSearchResultListItemTopic
          }
        }
      }
    `,
    reference,
  );

  const [isExpand, toggleIsExpand] = useToggle();

  const renderItems = useCallback(() => {
    if (isExpand) {
      return topics?.map(
        ({ node }) =>
          notEmpty(node.content) && (
            <StyledTopicSearchResultListItem key={node.content.entityId} reference={node.content} />
          ),
      );
    }
    const firstItem = first(topics)?.node;
    return (
      notEmpty(firstItem?.content) && (
        <StyledTopicSearchResultListItem reference={firstItem.content} />
      )
    );
  }, [isExpand, topics]);

  return (
    notEmpty(topics) && (
      <Container className={className}>
        <Title>Topic:</Title>

        {renderItems()}

        {topics.length > 1 && (
          <SearchResultListToggle
            isExpand={isExpand}
            onClick={() => toggleIsExpand()}
            toggleMessage={{
              after: "Show less",
              before: "Show all topics",
            }}
          />
        )}
      </Container>
    )
  );
};

TopicSearchResultList.displayName = "TopicSearchResultList";
